import React, { useState, useMemo, useEffect, useCallback } from 'react';
import DtvBox from '@directv-digital/dds.ui.dtv-box';
import Button from '@directv-digital/fusion.atoms.button';
import DtvTheme from '@directv-digital/dds.ui.dtv-theme';

const bps = DtvTheme.breakpoints;
import Recommendations from './components/recommendations';
import NumberButton from './components/number-button';
import QuizSection from './components/quiz-section';
import { EquipmentWizardProps } from './equipment-wizard.props';
import DtvModal from '@directv-digital/dds.ui.dtv-modal';
import RedirectModal from './components/redirect-modal';
import { getSavedCart } from 'src/utils/UtilityFunctions';
import { fireCustomLinkClickGA, getAnalyticsAttrs } from "@directv-digital/dds.services.analytics-service";

const transitionStyles = {
  fadeIn: {
    opacity: 1,
    transition: "opacity 300ms ease-in 100ms"
  },
  fadeOut: {
    opacity: 0,
    transition: "opacity 300ms ease-out",
    position: "absolute",
    zIndex: -1,
    display: "none"
  }
};

export const EquipmentWizard = (props: EquipmentWizardProps) => {
  const {
    id,
    q1Options, q2Options,
    requiredContent, content, stylingOptions
  } = props;
  const {
    q1Heading,
    q2HeadingPlural,
    q2HeadingSingular,
    optionsListPlural,
    optionsListSingular,
    bulletsHeroImg,
    bulletsList
  } = requiredContent || {};
  const {
    quizHeading,
    quizSubheading,
    q1CtaLabel,
    q2CtaLabel,
    bulletsHeading,
    bulletsSubheading,
    optionsHeading,
    optionsSubheading,
    optionsSaveCtaLabelDefault,
    optionsSaveCtaLabelConfirmed,
    optionsSaveConfirmationMsg,
    redirectModalHeading,
    redirectModalSubheading,
    redirectModalPrimaryCtaLabel,
    redirectModalTargetUrl,
    redirectModalSecondaryCtaLabel
  } = content;
  const {
    componentBgColor,
    componentBorderColor,
    cardBgColor
  } = stylingOptions;

  const [tvsSelected, setTvsSelected] = useState<number | null>(null);
  const [devicesSelected, setDevicesSelected] = useState<number | null>(null);
  const [showQ2, setShowQ2] = useState(false);
  const [quizFinished, setQuizFinished] = useState(false);
  const [redirectModalOpen, setNoCartModalOpen] = useState(false);
  const [hasCartItems, setHasCartItems] = useState(false);

  const Q1Buttons = useMemo(() => {
    const buttons = [];
    for (let ix = 0; ix < q1Options.length; ix += 3) {
      const btnGroup = q1Options.slice(ix, ix + 3);
      buttons.push(
        <DtvBox key={`q1-${ix}-${ix + 3}`} sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 63px)",
          gap: "16px"
        }}>
          {btnGroup.map(option => {
            const select = tvsSelected !== null && tvsSelected === option.value;
            return (
              <NumberButton
                key={`q1-${option.label}`}
                label={option.label}
                selected={select}
                onClick={() => {
                  setTvsSelected(option.value);
                  setDevicesSelected(prev => (prev > option.value ? option.value : prev));
                }}
              />
            )
          })}
        </DtvBox>
      )
    }

    return buttons;
  }, [tvsSelected, q1Options]);

  // Update the in progress tvs selection on each Q1 question click
  useEffect(() => {
    sessionStorage?.removeItem("equipment-wizard-tvs");
    if (tvsSelected !== null) {
      sessionStorage?.setItem("equipment-wizard-tvs", tvsSelected.toString());
    }
  }, [tvsSelected]);

  const Q2Buttons = useMemo(() => q2Options.map((option, ix) => {
    const disable = tvsSelected !== null && option.value > tvsSelected;
    const select = devicesSelected !== null && option.value === devicesSelected;
    return (
        <NumberButton
          key={`q2-${option.value}-${ix}`}
          label={option.label}
          height={63} width={63}
          selected={select}
          disabled={disable}
          onClick={() => {
            setDevicesSelected(option.value)
          }}
        />
    );
  }), [tvsSelected, devicesSelected, q2Options]);

  // Update the in progress devices selection on each Q2 question click
  useEffect(() => {
    sessionStorage?.removeItem("equipment-wizard-devices");
    if (devicesSelected !== null) {
      sessionStorage.setItem("equipment-wizard-devices", devicesSelected.toString())
    }
  }, [devicesSelected]);

  const onHandleRedirectModalClick = (label: any, url: any) => {
    if (label === undefined || label === null || label === "") {
      return ;
    }
    const dataForAnalytics = {
      event: 'trackEvent',
      event_name: 'custom_link_click',
      linkName: `${label?.toLowerCase()}`,
      linkPosition: 'modal_devicewizard',
      linkDestination: `${url}`
    }
    fireCustomLinkClickGA(dataForAnalytics);
  }
  const handleOnClickEvent = (qNo: any ) => {
    const dataForAnalytics = {
      event: 'trackEvent',
      event_name: 'custom_link_click',
      linkName: `${qNo?.toLowerCase()}`,
      linkPosition: 'panel_devicewizard',
      linkDestination: `${window?.location?.origin}${window?.location?.pathname}`
    }
    fireCustomLinkClickGA(dataForAnalytics);
  };
  const Q1NextButton = useMemo(() => (
    <Button
      buttontype='primary' mode='light'
      size='medium'
      disabled={tvsSelected === null}
      {...getAnalyticsAttrs(`question1_${q1CtaLabel}`.toLowerCase(), 'panel_devicewizard', typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname}` : undefined)}
      onClick={() => {
        setShowQ2(true);
        const q2Ctnr = document?.querySelector(`#${id}-q2-ctnr`);
        setTimeout(() => {
          if (q2Ctnr) {
 q2Ctnr?.scrollIntoView({ block: "nearest" });
}
        }, 400);
      }}
      sx={{
        transition: 'height 200ms ease-in 300ms, background-color 150ms ease-in',
        height: showQ2 === false ? '42px' : '0px',
        margin: 0

      }}
    >{q1CtaLabel}
    </Button>
  ), [q1CtaLabel, tvsSelected, showQ2]);

  const Q2NextButton = useMemo(() => (
    <Button
      buttontype='primary' mode='light'
      size='medium'
      disabled={devicesSelected === null}
      {...getAnalyticsAttrs(`question2_${q2CtaLabel}`.toLowerCase(), 'panel_devicewizard', typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname}` : undefined)}
      sx={{
        transition: 'background-color 150ms ease-in',
        height: "42px",
        margin: 0

      }}
      onClick={() => {
        if (tvsSelected !== null && devicesSelected !== null) {
          setQuizFinished(true);
        }
      }}
    >{q2CtaLabel}
    </Button>
  ), [q2CtaLabel, devicesSelected]);

  const radioOptions = useMemo(() => {
    const options = [];
    if (typeof tvsSelected !== 'number' || typeof devicesSelected !== 'number') {
      return options;
    }

    if (tvsSelected === 1) {
      optionsListSingular?.forEach((option, ix) => {
        if (option?.includes("[tv_devices_diff]") && devicesSelected === 0) {
          options.push({
            label: option?.replace(/\[tv_devices_diff\]|\[tvs_total\]/g, () => "1"),
            value: ix,
            devices: 1
          });
        } else if (option?.includes("[tvs_total]") && devicesSelected === 1) {
          options.push({
            label: option?.replace(/\[tv_devices_diff\]|\[tvs_total\]/g, () => "1"),
            value: ix,
            devices: 1
          });
        } else if (!(option?.includes("[") || option?.includes("]"))) {
          options.push({
            label: option,
            value: ix,
            addLater: true
          });
        }
      })
      return options;
    }
    const injectMap = {
      "[tv_devices_diff]": Number(tvsSelected - devicesSelected),
      "[tvs_total]": tvsSelected
    };
    // eslint-disable-next-line no-console
    console.debug("EquipmentWizard ## generating multi-tv radios with",
      "injectMap", injectMap, `tvs: ${tvsSelected}`, `devices: ${devicesSelected}`);
    optionsListPlural?.forEach((option, ix) => {
      if (option?.includes("[tv_devices_diff]")) {
        // ignore diff IF --> TVs - devices = devices OR TVs - devices = 0
        const ignoreCase1 = injectMap["[tv_devices_diff]"] === injectMap["[tvs_total]"];
        const ignoreCase2 = injectMap["[tv_devices_diff]"] === 0;
        if (!ignoreCase1 && !ignoreCase2) {
          options.push({
            label: option.replace(/\[tv_devices_diff\]|\[tvs_total\]/g, matched => injectMap[matched]?.toString()),
            value: ix,
            devices: injectMap["[tv_devices_diff]"]
          });
        }
      } else if (option?.includes("[tvs_total]")) {
        options.push({
          label: option.replace(/\[tv_devices_diff\]|\[tvs_total\]/g, matched => injectMap[matched]?.toString()),
          value: ix,
          devices: injectMap["[tvs_total]"]
        })
      } else if (!(option?.includes("[") || option?.includes("]"))) {
        options.push({
          label: option,
          value: ix,
          addLater: true
        });
      }
    });
    return options;
  }, [tvsSelected, devicesSelected, optionsListPlural, optionsListSingular]);

  // wrapped in useCallback to avoid recreating function when sent to a memoized component
  const onBackClickQuiz = useCallback(() => {
    handleOnClickEvent(`question2_back`);
    setShowQ2(false);
    const q1Ctnr = document?.querySelector(`#${id}-q1-ctnr`);
    setTimeout(() => {
      if (q1Ctnr) {
        q1Ctnr?.scrollIntoView({ block: "nearest" });
      }
    }, 400);
  }, []);

  const onBackClickRecommendations = useCallback((labelName?: any, isRestart?: boolean) => {
    handleOnClickEvent(`question3_${labelName}`);
    setQuizFinished(false);
    setShowQ2(true);
    if (isRestart) {
      setTvsSelected(null);
      setDevicesSelected(null);
    }
  }, []);

  const handleToggleRedirectModal = useCallback((label?: any) => {
    setNoCartModalOpen(prev => !prev);
    if (label) {
      onHandleRedirectModalClick(label, `${window?.location?.origin}${window?.location?.pathname}`)
      console.warn('redirectModalSecondaryCta label -> ', label);
    }
  }, []);

  useEffect(() => {
    if (localStorage !== undefined) {
      setHasCartItems(!!getSavedCart());
    }
    if (sessionStorage !== undefined) {
      // get the existing answers from sessionStorage
      const sessionTvs = sessionStorage.getItem("equipment-wizard-tvs");
      const sessionDevices = sessionStorage.getItem("equipment-wizard-devices");
      const deviceIntent = sessionStorage.getItem("device-intent");

      // If user has completed the quiz and coming back, start over
      if (deviceIntent !== null) {
        sessionStorage?.removeItem("equipment-wizard-tvs");
        sessionStorage?.removeItem("equipment-wizard-devices");
        return;
      }

      // set values and clear sessionStorage if they exist so new values can be added
      if (sessionTvs !== null) {
        sessionStorage?.removeItem("equipment-wizard-tvs");
        setTvsSelected(Number(sessionTvs || ""));
        setShowQ2(true);
        // eslint-disable-next-line no-console
        console.debug("EquipmentWizard ## loaded tvsSelected from sessionStorage", sessionTvs);

        if (sessionDevices !== null) {
          sessionStorage?.removeItem("equipment-wizard-devices");
          // eslint-disable-next-line no-console
          console.debug("EquipmentWizard ## loaded devicesSelected from sessionStorage", sessionDevices);

          if (Number(sessionDevices) <= Number(sessionTvs)) {
            setDevicesSelected(Number(sessionDevices));
          }
        }
      }
    }

    // save entries on unmount
    return () => {
      if (tvsSelected !== null) {
        sessionStorage?.removeItem("equipment-wizard-tvs");
        sessionStorage.setItem("equipment-wizard-tvs", tvsSelected?.toString())
      }
      if (devicesSelected !== null) {
        sessionStorage?.removeItem("equipment-wizard-devices");
        sessionStorage.setItem("equipment-wizard-devices", devicesSelected?.toString())
      }
    }
  }, []);

  // eslint-disable-next-line no-console
  console.debug("EquipmentWizard ## rendering...",
    `showQ2: ${showQ2}`,
    `quizFinished: ${quizFinished}`,
    `tvsSelected: ${tvsSelected}`,
    `devicesSelected: ${devicesSelected}`,
    `radioOptions:`, radioOptions);

  return (
    <DtvBox id={`${id}-root`}
      sx={{
        fontFamily: "PFDinMed",
        padding: "36px 0 20px 0px",
        margin: '0 20px',
        [bps.between(bps.values.sm, bps.values.md)]: {
          padding: "48px 0 28px 0",
          margin: '0 30px'
        },
        [bps.up(bps.values.md)]: {
          padding: "66px 0 36px 0",
          margin: '0 100px'
        },
        position: "relative",
minHeight: "300px"
      }}>
        <DtvBox sx={{
          backgroundColor: componentBgColor,
          border: `1px solid ${componentBorderColor}`,
borderRadius: "8px",
          ...!quizFinished ? transitionStyles.fadeIn : transitionStyles.fadeOut
        }}>
          <QuizSection
              id={id}
              quizHeading={quizHeading}
              quizSubheading={quizSubheading}
              cardBgColor={cardBgColor}
              showQ2={showQ2}
              tvsSelected={tvsSelected}
              q1Heading={q1Heading}
              q2HeadingPlural={q2HeadingPlural}
              q2HeadingSingular={q2HeadingSingular}
              Q1Buttons={Q1Buttons}
              Q2Buttons={Q2Buttons}
              Q1NextButton={Q1NextButton}
              Q2NextButton={Q2NextButton}
              onBackButtonClickFn={onBackClickQuiz}
            />
        </DtvBox>
        <DtvBox sx={{
          backgroundColor: componentBgColor,
          border: `1px solid ${componentBorderColor}`,
borderRadius: "8px",
          ...quizFinished ? transitionStyles.fadeIn : transitionStyles.fadeOut
        }}>
          <Recommendations
            id={id}
            recommendationsBgColor={cardBgColor}
            bulletsHeroImg={bulletsHeroImg}
            bulletsHeading={bulletsHeading}
            bulletsSubheading={bulletsSubheading}
            bulletsList={bulletsList}
            optionsHeading={optionsHeading}
            optionsSubheading={optionsSubheading}
            radioOptions={radioOptions}
            saveCtaLabelDefault={optionsSaveCtaLabelDefault}
            saveCtaLabelConfirmed={optionsSaveCtaLabelConfirmed}
            saveCtaConfirmationMsg={optionsSaveConfirmationMsg}
            hasCartItems={hasCartItems}
            handleToggleRedirectModal={handleToggleRedirectModal}
            onRestartClickFn={onBackClickRecommendations}
          />
        </DtvBox>

        <DtvModal
          open={redirectModalOpen}
          handleClose={handleToggleRedirectModal}
          modalStyle={{
            maxHeight: 'min-content'
          }}
          modalBodyStyle={{
            [bps.between(bps.values.sm, bps.values.md)]: {
              maxHeight: '40vh'
            },
            [bps.up(bps.values.md)]: {
              maxHeight: '50vh'
            }
          }}
        >
          <RedirectModal
            id={id}
            redirectModalHeading={redirectModalHeading}
            redirectModalSubheading={redirectModalSubheading}
            redirectModalPrimaryCta={redirectModalPrimaryCtaLabel}
            redirectModalSecondaryCta={redirectModalSecondaryCtaLabel}
            redirectModalTargetUrl={redirectModalTargetUrl}
            handleToggleRedirectModal={handleToggleRedirectModal}
            handleRedirectModalClick={onHandleRedirectModalClick}
          />
        </DtvModal>
    </DtvBox>
  );
};
